import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Heart, Users, Globe, Award, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import Marquee from '../components/Marquee';
import StarBadge from '../components/StarBadge';

const About = () => {
  const values = [
    {
      icon: '💖',
      title: 'Passion for Quality',
      description: 'We use only the finest ingredients to create our signature frozen yogurt and fresh fruit toppings.'
    },
    {
      icon: '🤝',
      title: 'Community First',
      description: "RUSH is more than a brand - it's a gathering place for families, friends, and frozen yogurt lovers."
    },
    {
      icon: '🌍',
      title: 'Global Vision',
      description: "From Nigeria to Lithuania, we're bringing the joy of premium frozen yogurt to communities worldwide."
    },
    {
      icon: '🏆',
      title: 'Excellence Always',
      description: 'Every cup of RUSH represents our commitment to delivering an exceptional experience.'
    }
  ];

  const stats = [
    { number: '3', label: 'Countries' },
    { number: '10K+', label: 'Happy Customers' },
    { number: '50+', label: 'Flavor Combos' },
    { number: '100%', label: 'Fresh Daily' }
  ];

  return (
    <div className="min-h-screen pt-28 bg-rush-cream">
      {/* Hero Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="inline-flex items-center bg-rush-mint/30 text-rush-navy rounded-full px-4 py-2 mb-6">
                <Sparkles size={16} className="mr-2" />
                <span className="text-sm font-semibold">Our Story</span>
              </span>
              <h1 className="text-5xl md:text-6xl font-display text-rush-navy mb-6">
                chill out<br />
                <span className="text-rush-blue">with rush!</span>
              </h1>
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                RUSH Frozen Yogurt brings you the freshest, most delicious frozen yogurt experience across Africa and Europe. Our signature creamy yogurt paired with premium fruit toppings creates moments of pure joy. 🍦
              </p>
              <p className="text-gray-600 text-lg leading-relaxed">
                Born from a love of creamy, delicious frozen treats, RUSH has grown from a single store to an international presence spanning Africa and Europe. Our mission remains simple: bring happiness one cup at a time.
              </p>
            </div>
            <div className="relative">
              <div className="absolute -top-6 -right-6 z-20">
                <StarBadge text="SINCE 2020" color="bg-rush-pink" size="lg" />
              </div>
              <div className="absolute inset-0 bg-rush-blue/20 rounded-3xl blur-3xl scale-90" />
              <img
                src="https://customer-assets.emergentagent.com/job_77a0e81f-8f8a-48d0-b8f5-7f6a2e32faae/artifacts/gofnk483_create-a-frozen-yougurt-shop-called-rush-blue-colo.png"
                alt="RUSH Store"
                className="relative z-10 rounded-3xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Marquee */}
      <Marquee text="FROZEN HAPPINESS" bgColor="bg-rush-blue" />

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, idx) => (
              <div key={idx} className="text-center">
                <p className="text-5xl md:text-6xl font-display text-rush-blue mb-2">{stat.number}</p>
                <p className="text-gray-500 font-medium">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-rush-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-display text-rush-navy mb-4">
              what we stand for
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Our values guide everything we do at RUSH
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div
                key={index}
                className="bg-white rounded-3xl p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-2 group"
              >
                <span className="text-5xl mb-6 block group-hover:scale-110 transition-transform">{value.icon}</span>
                <h3 className="text-xl font-bold text-rush-navy mb-3">{value.title}</h3>
                <p className="text-gray-600 leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Container Store Section */}
      <section className="py-20 bg-rush-navy">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <div className="absolute -bottom-6 -left-6 z-20">
                <StarBadge text="UNIQUE DESIGN" color="bg-rush-mint" textColor="text-rush-navy" size="lg" />
              </div>
              <img
                src="https://customer-assets.emergentagent.com/job_77a0e81f-8f8a-48d0-b8f5-7f6a2e32faae/artifacts/9x1qyur6_create-a-store-for-rush--a-frozen-yogurt-shop--blu%20%281%29.png"
                alt="RUSH Container Store"
                className="rounded-3xl shadow-2xl"
              />
            </div>
            <div>
              <h2 className="text-4xl md:text-5xl font-display text-white mb-6">
                unique store experience
              </h2>
              <p className="text-white/80 text-lg leading-relaxed mb-6">
                Our innovative container stores bring the RUSH experience to unexpected places. From urban centers to coastal retreats, we're redefining what a frozen yogurt shop can be. 🏪
              </p>
              <p className="text-white/80 text-lg leading-relaxed">
                Each location features our signature vibrant blue aesthetic, creating an Instagram-worthy backdrop for your frozen yogurt moments. 📸
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Marquee */}
      <Marquee text="TASTE THE JOY" bgColor="bg-rush-pink" />

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-display text-rush-navy mb-6">
            ready to experience rush?
          </h2>
          <p className="text-gray-600 text-lg mb-8 max-w-2xl mx-auto">
            Find your nearest location and discover why RUSH is the go-to destination for frozen yogurt lovers.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/locations">
              <Button className="bg-rush-blue hover:bg-rush-blue/90 text-white font-bold px-8 py-6 text-lg rounded-full group">
                Find a Location
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
              </Button>
            </Link>
            <Link to="/franchise">
              <Button variant="outline" className="border-2 border-rush-pink text-rush-pink hover:bg-rush-pink hover:text-white font-bold px-8 py-6 text-lg rounded-full">
                Franchise Opportunities
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
